// Reports tab: "Most Profitable Job Type", "Worst Performing Jobs" and "Client Profitability Ranking".
// Pure functions (no React, no window) so the numbers and wording can be tested.
// Money is added up in whole cents (toCents) and turned back into dollars once, at the end,
// so float traps like 0.1 + 0.2 never reach the screen, the PDF or the Excel file.
// Margin is profit / revenue and only exists when revenue is above 0; otherwise it is null.

import { toCents, marginPercent } from "./aiCharts";

export const NO_CLIENT = "No client";
export const NO_JOB_TYPE = "No job type";
export const NO_JOB_NAME = "Unnamed job";

const nameOr = (v, fallback) => (typeof v === "string" && v.trim() !== "" ? v.trim() : fallback);
const jobList = (jobs) => (Array.isArray(jobs) ? jobs.filter(Boolean) : []);
const dollars = (cents) => (cents === 0 ? 0 : cents / 100);
const byName = (a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);

// ─── Most Profitable Job Type ────────────────────────────────────────────────

// One row per job type: { name, margin, profit, jobs }. margin is a percent with one decimal, or null
// when the type has no revenue. Highest margin first; types with no margin go last; ties go to the
// larger profit, then the name.
export function jobTypeRows(jobs) {
  const groups = new Map();
  jobList(jobs).forEach((j) => {
    const name = nameOr(j.type, NO_JOB_TYPE);
    const g = groups.get(name) || { name, rev: 0, cost: 0, jobs: 0 };
    g.rev += toCents(j.revenue);
    g.cost += toCents(j.costs);
    g.jobs += 1;
    groups.set(name, g);
  });
  return [...groups.values()]
    .map((g) => ({ name: g.name, margin: marginPercent(g.rev - g.cost, g.rev), profit: dollars(g.rev - g.cost), jobs: g.jobs }))
    .sort((a, b) => {
      if ((a.margin === null) !== (b.margin === null)) return a.margin === null ? 1 : -1;
      if (a.margin !== b.margin) return b.margin - a.margin;
      return b.profit - a.profit || byName(a, b);
    });
}

const jobsRef = (name) => (name === NO_JOB_TYPE ? "jobs with no job type" : `${name} jobs`);
const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function jobTypeInsight(data, money) {
  const rows = Array.isArray(data) ? data : [];
  if (rows.length === 0) return "There are no jobs to compare yet. Once you have jobs, Canopy will show which type of work earns you the most.";
  const ranked = rows.filter((r) => typeof r.margin === "number");
  if (ranked.length === 0) return "None of your job types have revenue yet, so there is no margin to compare. Once you invoice some work, Canopy will show which type earns you the most.";
  const best = ranked[0];
  if (ranked.length === 1) {
    return `Your margin is ${best.margin}% on ${jobsRef(best.name)} (${money(best.profit)} profit across ${plural(best.jobs, "job", "jobs")}). Add jobs with another job type and Canopy can show how they compare.`;
  }
  const worst = ranked[ranked.length - 1];
  const gap = (Math.round((best.margin - worst.margin) * 10) / 10).toFixed(1);
  if (best.margin === worst.margin) {
    return `Your job types are all earning the same ${best.margin}% margin, so none stands out yet. Looking at pricing and costs on each could show where there is room to improve.`;
  }
  const lead = `Your highest margin, ${best.margin}%, is on ${jobsRef(best.name)}, ${gap} points ahead of ${jobsRef(worst.name)} (${worst.margin}%).`;
  const next = worst.margin < 0
    ? `${cap(jobsRef(worst.name))} are losing money (${money(worst.profit)}), so they may be the best place to look at pricing and costs first. You might also consider going after more ${jobsRef(best.name)}.`
    : `You might consider going after more ${jobsRef(best.name)}, and checking whether pricing or costs on ${jobsRef(worst.name)} could improve.`;
  return `${lead} ${next}`;
}

// ─── Worst Performing Jobs ───────────────────────────────────────────────────

// The five lowest-profit jobs, lowest first: { name, profit, margin }. margin is null without revenue.
// Ties go to the name so the order is stable.
export function worstJobRows(jobs, count = 5) {
  return jobList(jobs)
    .map((j) => {
      const rev = toCents(j.revenue);
      const cost = toCents(j.costs);
      return { name: nameOr(j.name, NO_JOB_NAME), cents: rev - cost, margin: marginPercent(rev - cost, rev) };
    })
    .sort((a, b) => a.cents - b.cents || byName(a, b))
    .slice(0, count)
    .map((r) => ({ name: r.name, profit: dollars(r.cents), margin: r.margin }));
}

export function worstJobsInsight(data, money) {
  const rows = Array.isArray(data) ? data : [];
  if (rows.length === 0) return "There are no jobs to review yet.";
  const worst = rows[0];
  const lossCents = rows.reduce((s, r) => s + (toCents(r.profit) < 0 ? -toCents(r.profit) : 0), 0);
  const lossCount = rows.filter((r) => toCents(r.profit) < 0).length;
  if (lossCount === 0) {
    if (rows.length === 1) {
      const one = toCents(worst.profit) === 0 ? "broke even" : `made ${money(worst.profit)}`;
      return `Your only job, ${worst.name}, didn't lose money: it ${one}. If you want to improve, look at whether pricing or costs on this job could be tuned.`;
    }
    const how = toCents(worst.profit) === 0 ? "broke even" : `still made ${money(worst.profit)}`;
    return `None of your ${rows.length} lowest-profit jobs lost money. The lowest is ${worst.name}, which ${how}. If you want to improve, look at whether pricing or costs on these jobs could be tuned.`;
  }
  const first = `Your lowest-profit job is ${worst.name}, at ${money(worst.profit)}.`;
  const losses = rows.length === 1
    ? `It lost money, ${money(lossCents / 100)} in total.`
    : lossCount === 1
      ? `It is the only one of your ${rows.length} lowest-profit jobs that lost money.`
      : `${lossCount} of your ${rows.length} lowest-profit jobs lost money, ${money(lossCents / 100)} in total.`;
  return `${first} ${losses} It may help to compare what ${rows.length === 1 ? "this job was" : "each job was"} priced at with what it cost, and to consider adjusting pricing or keeping a closer eye on costs for similar work.`;
}

// ─── Client Profitability Ranking ────────────────────────────────────────────

// One row per client: { name, profit, revenue }, most profit first (ties by name).
export function clientRows(jobs) {
  const groups = new Map();
  jobList(jobs).forEach((j) => {
    const name = nameOr(j.clientName, NO_CLIENT);
    const g = groups.get(name) || { name, rev: 0, cost: 0 };
    g.rev += toCents(j.revenue);
    g.cost += toCents(j.costs);
    groups.set(name, g);
  });
  return [...groups.values()]
    .map((g) => ({ name: g.name, cents: g.rev - g.cost, revenue: dollars(g.rev) }))
    .sort((a, b) => b.cents - a.cents || byName(a, b))
    .map((g) => ({ name: g.name, profit: dollars(g.cents), revenue: g.revenue }));
}

// Share of total profit as a whole percent: top client profit / total profit, only when both are
// above 0 and the top client is not more than the total. Otherwise null.
export function profitSharePercent(topProfit, totalProfit) {
  const top = toCents(topProfit);
  const total = toCents(totalProfit);
  if (!(top > 0) || !(total > 0) || top > total) return null;
  return Math.round((top * 100) / total);
}

export function clientInsight(data, money) {
  const rows = Array.isArray(data) ? data : [];
  if (rows.length === 0) return "There are no clients to rank yet.";
  const top = rows[0];
  const topCents = toCents(top.profit);
  if (topCents <= 0) {
    return `No client has made a profit yet. ${top.name} is closest at ${money(topCents / 100)}. A look at pricing and costs on these jobs could show where to improve.`;
  }
  if (rows.length === 1) {
    return `${top.name} is your only client so far, generating ${money(topCents / 100)} profit. Adding more clients can help you see which relationships work best.`;
  }
  const totalCents = rows.reduce((s, r) => s + toCents(r.profit), 0);
  const pct = profitSharePercent(topCents / 100, totalCents / 100);
  const tail = "Consider what makes this relationship work, and whether similar clients could be a good fit.";
  if (pct !== null) return `${top.name} is your most profitable client, generating ${money(topCents / 100)}, which is ${pct}% of your total profit. ${tail}`;
  if (totalCents > 0) {
    return `${top.name} is your most profitable client, generating ${money(topCents / 100)}. That is more than your total profit of ${money(totalCents / 100)}, because some clients cost more than they brought in. ${tail}`;
  }
  return `${top.name} is your most profitable client, generating ${money(topCents / 100)}. Your profit across all clients is ${money(totalCents / 100)}, so some clients are costing more than they bring in. Those may be worth a closer look. ${tail}`;
}
